/**
 * Volunteer messages (docs/VOLUNTEER_SIGNUP.md ruling 39;
 * `20261021120000_volunteer_messages.sql`): an organiser emails everyone on a
 * project, a role lead emails their role, and the volunteer app shows the
 * newest messages as a "From <lead>" card.
 *
 * The write contract (`POST …/messages`), the read wire (preview + history),
 * the public unsubscribe body and the coded errors. The SQL is the authority
 * for every rule (sender, recipients, limits, cap); the constants here mirror
 * it for the UI and the API.
 */
import { z } from "zod";
import { surveyText } from "./surveys.js";
import { VOLUNTEER_ERROR_CODES, VOLUNTEER_LOCALES } from "./volunteerSignupReads.js";
// ---------------------------------------------------------------------------
// Constants (mirrors of the SQL)
// ---------------------------------------------------------------------------
/** `role` (one role; admins and that role's leads) or `everyone` (org admins only). No per-shift email. */
export const VOLUNTEER_MESSAGE_SCOPES = ["role", "everyone"];
/** `all` of the role, or `led`: the role's shifts at the events an EVENT lead leads (only they choose). */
export const VOLUNTEER_MESSAGE_AUDIENCES = ["all", "led"];
/** What the caller may send (`volunteer_message_sender_scope`). */
export const VOLUNTEER_MESSAGE_SENDER_SCOPES = ["admin", "role_lead", "event_lead", "none"];
export const VOLUNTEER_MESSAGE_SUBJECT_MAX = 200;
export const VOLUNTEER_MESSAGE_BODY_MAX = 10_000;
/** At most this many recipients per send; above it the send is refused (RV024), never cut short. */
export const VOLUNTEER_MESSAGE_RECIPIENT_CAP = 500;
/** Sends per rolling window: 5 per role, 3 to everyone (RV022). */
export const VOLUNTEER_MESSAGE_LIMITS = { role: 5, everyone: 3 };
export const VOLUNTEER_MESSAGE_WINDOW_HOURS = 24;
/** Recipients hold a shift still to come or that ended at most this many days ago. */
export const VOLUNTEER_MESSAGE_RECENT_DAYS = 7;
/** A queued / sent row older than this without an SES event counts as "not confirmed". */
export const VOLUNTEER_MESSAGE_PENDING_MINUTES = 15;
/** The history page size (`GET …/messages`). */
export const VOLUNTEER_MESSAGE_HISTORY_LIMIT = 20;
/** The newest messages a volunteer's `me` carries. */
export const VOLUNTEER_PUBLIC_MESSAGES_MAX = 5;
/** The raw one-click unsubscribe token (64 hex, generated in SQL; only its sha256 is stored). */
export const VOLUNTEER_UNSUBSCRIBE_TOKEN_RE = /^[0-9a-f]{64}$/;
export function isVolunteerUnsubscribeToken(value) {
    return typeof value === "string" && VOLUNTEER_UNSUBSCRIBE_TOKEN_RE.test(value);
}
/** The SES message tags the dispatcher sets and the events function reads. */
export const VOLUNTEER_MESSAGE_SES_TAGS = { message: "vm_message", recipient: "vm_recipient" };
// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------
const uuid = z.string().uuid();
/** The fields a crew email is written with (the send and the test share them). */
const volunteerMessageFields = {
    scope: z.enum(VOLUNTEER_MESSAGE_SCOPES),
    role_id: uuid.nullable().optional(),
    audience: z.enum(VOLUNTEER_MESSAGE_AUDIENCES).default("all"),
    include_waitlist: z.boolean().default(false),
    /** The sender opts in to replies (Reply-To = their account email); off by default. */
    allow_replies: z.boolean().default(false),
    subject: surveyText(VOLUNTEER_MESSAGE_SUBJECT_MAX, { min: 1 }),
    body: surveyText(VOLUNTEER_MESSAGE_BODY_MAX, { multiline: true }).default(""),
};
/** `role_id` is required for `role` and refused for `everyone`. */
function refineScopeRole(v, ctx) {
    if (v.scope === "role" && !v.role_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_id"], message: "A role message needs role_id" });
    }
    if (v.scope === "everyone" && v.role_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_id"], message: "An everyone message takes no role" });
    }
}
/**
 * POST …/messages. Strict. `role_id` is required for `role` and refused for
 * `everyone`; `audience: "led"` only with `role` (the SQL further refuses it
 * unless the caller is an event lead). The subject is one line (newlines are
 * scrubbed to spaces), required; the body is the email Markdown subset,
 * possibly empty.
 */
export const volunteerMessageSendSchema = z
    .object(volunteerMessageFields)
    .strict()
    .superRefine((v, ctx) => {
    refineScopeRole(v, ctx);
    if (v.scope === "everyone" && v.audience !== "all") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["audience"], message: "Only a role message has an audience" });
    }
});
/**
 * POST …/messages/test: the message as written, emailed only to the caller's
 * own account address (never chosen by the client). The same scope / role /
 * subject / body rules as the send; no audience or waitlist (one recipient).
 * `allow_replies` follows the compose tick, so the test shows the Reply-To and
 * footer the volunteers will get. `locale` is the sender's UI language (the email chrome and the `[Test]` prefix).
 */
export const volunteerMessageTestSchema = z
    .object({
    scope: volunteerMessageFields.scope,
    role_id: volunteerMessageFields.role_id,
    subject: volunteerMessageFields.subject,
    body: volunteerMessageFields.body,
    allow_replies: volunteerMessageFields.allow_replies,
    locale: z.enum(VOLUNTEER_LOCALES).default("en"),
})
    .strict()
    .superRefine(refineScopeRole);
/** GET …/messages?scope=role&role_id=… | ?scope=everyone. */
export const volunteerMessageListQuerySchema = z
    .object({ scope: z.enum(VOLUNTEER_MESSAGE_SCOPES), role_id: uuid.optional() })
    .strict()
    .superRefine((v, ctx) => {
    if (v.scope === "role" && !v.role_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_id"], message: "A role history needs role_id" });
    }
    if (v.scope === "everyone" && v.role_id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["role_id"], message: "The everyone history takes no role" });
    }
});
/** POST /api/v1/public/volunteer-unsubscribe — the page's read (confirm false) and confirm (true). */
export const volunteerUnsubscribeSchema = z
    .object({
    token: z.string().regex(VOLUNTEER_UNSUBSCRIBE_TOKEN_RE, "not an unsubscribe token"),
    confirm: z.boolean().default(false),
})
    .strict();
// ---------------------------------------------------------------------------
// Coded errors
// ---------------------------------------------------------------------------
/** RV021..RV024 → HTTP status + coded envelope (the API maps; the web reads `code`). */
export const VOLUNTEER_MESSAGE_SQLSTATE_ERRORS = {
    RV021: { status: 403, code: VOLUNTEER_ERROR_CODES.messageNotAllowed },
    RV022: { status: 429, code: VOLUNTEER_ERROR_CODES.messageLimit },
    RV023: { status: 409, code: VOLUNTEER_ERROR_CODES.messageNoRecipients },
    RV024: { status: 422, code: VOLUNTEER_ERROR_CODES.messageTooMany },
};
/** `VOLUNTEER_MESSAGE_LIMIT|<retry_at ISO>|<limit>` (RV022), or null. */
export function parseVolunteerMessageLimit(message) {
    if (typeof message !== "string")
        return null;
    const m = /VOLUNTEER_MESSAGE_LIMIT\|(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)\|(\d+)/.exec(message);
    return m ? { retryAt: m[1], limit: Number(m[2]) } : null;
}
/** `VOLUNTEER_MESSAGE_TOO_MANY|<count>|<cap>` (RV024), or null. */
export function parseVolunteerMessageTooMany(message) {
    if (typeof message !== "string")
        return null;
    const m = /VOLUNTEER_MESSAGE_TOO_MANY\|(\d+)\|(\d+)/.exec(message);
    return m ? { count: Number(m[1]), cap: Number(m[2]) } : null;
}
//# sourceMappingURL=volunteerMessages.js.map